import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Settings, Edit2, Trash2, Users, Save, X } from 'lucide-react';
import { getProject, updateProject, deleteProject } from '../lib/projects';
import type { Project } from '../../../shared/lib/supabase';
import { toast } from 'sonner';

export function ProjectSettingsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    if (projectId) {
      loadProject();
    }
  }, [projectId]);

  const loadProject = async () => {
    if (!projectId) return;
    
    try {
      const projectData = await getProject(projectId);
      setProject(projectData);
      setProjectName(projectData.name);
      setProjectDescription(projectData.description || '');
    } catch (error) {
      console.error('Error loading project:', error);
      toast.error('Failed to load project settings');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!project || !projectId) return;

    try {
      await updateProject(projectId, {
        name: projectName,
        description: projectDescription
      });
      
      setProject({
        ...project,
        name: projectName,
        description: projectDescription
      });
      
      setIsEditing(false);
      toast.success('Project settings updated');
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Failed to update project settings');
    }
  };

  const handleDelete = async () => {
    if (!projectId || deleteConfirmText !== project?.name) return;

    try {
      await deleteProject(projectId);
      toast.success('Project deleted successfully');
      navigate('/projects');
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  if (!project) {
    return <div>Project not found</div>;
  }

  return (
    <div className="min-h-full bg-white">
      <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <Settings className="w-6 h-6" />
          Project Settings
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage your project configuration and settings
        </p>
      </div>

      {/* General Settings */}
      <div className="bg-white rounded-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">General</h2>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setProjectName(project.name);
                  setProjectDescription(project.description || '');
                  setIsEditing(false);
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-white bg-gray-900 hover:bg-gray-800 rounded-md transition-colors"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Name
            </label>
            {isEditing ? (
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              />
            ) : (
              <p className="text-gray-900">{project.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            {isEditing ? (
              <textarea
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                placeholder="Add a description for your project..."
              />
            ) : (
              <p className="text-gray-900">
                {project.description || <span className="text-gray-500 italic">No description</span>}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Workflow Type
            </label>
            <p className="text-gray-900 capitalize">{project.workflow_type}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Created
            </label>
            <p className="text-gray-900">
              {new Date(project.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Team Members */}
      <div className="bg-white rounded-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Team Members
          </h2>
        </div>
        <p className="text-sm text-gray-500">
          Project members are managed at the team level. Go to team settings to manage members.
        </p>
      </div>

      {/* Danger Zone */}
      <div className="bg-white rounded-sm border border-red-200 p-6">
        <h2 className="text-lg font-medium text-red-900 mb-4">Danger Zone</h2>
        
        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:text-red-700 border border-red-300 hover:border-red-400 rounded-md transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete Project
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              This action cannot be undone. This will permanently delete the project
              <span className="font-semibold"> {project.name}</span> and all associated data.
            </p>
            <p className="text-sm text-gray-700">
              Please type <span className="font-mono font-semibold">{project.name}</span> to confirm.
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Type project name to confirm"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteConfirmText !== project.name}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete Project
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}